import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, UserCheck, UserX, Edit, Trash2, Eye } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function EmployeesPage() {
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', fiscal_code: '', role_title: '', department: '', phone: '', email: '', hire_date: '', contract_level: '', date_of_birth: '', place_of_birth: '', employee_number: '', size_giubbotto: '', size_giacca: '', size_felpa: '', size_tshirt: '', size_camicia: '', size_pantalone: '', size_scarpe: '' });
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', fiscal_code: '', role_title: '', department: '', phone: '', email: '', hire_date: '', contract_level: '', is_active: true, date_of_birth: '', place_of_birth: '', employee_number: '', size_giubbotto: '', size_giacca: '', size_felpa: '', size_tshirt: '', size_camicia: '', size_pantalone: '', size_scarpe: '' });

  const load = async () => {
    try {
      const res = await api.get('/employees', { params: { search: search || undefined } });
      setEmployees(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search]);

  const handleCreate = async () => {
    try {
      await api.post('/employees', form);
      setShowCreate(false);
      setForm({ first_name: '', last_name: '', fiscal_code: '', role_title: '', department: '', phone: '', email: '', hire_date: '', contract_level: '', date_of_birth: '', place_of_birth: '', employee_number: '', size_giubbotto: '', size_giacca: '', size_felpa: '', size_tshirt: '', size_camicia: '', size_pantalone: '', size_scarpe: '' });
      load();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (emp, e) => {
    e.stopPropagation();
    setSelectedEmployee(emp);
    setEditForm({
      first_name: emp.first_name,
      last_name: emp.last_name,
      fiscal_code: emp.fiscal_code || '',
      role_title: emp.role_title || '',
      department: emp.department || '',
      phone: emp.phone || '',
      email: emp.email || '',
      hire_date: emp.hire_date || '',
      contract_level: emp.contract_level || '',
      is_active: emp.is_active,
      date_of_birth: emp.date_of_birth || '',
      place_of_birth: emp.place_of_birth || '',
      employee_number: emp.employee_number || '',
      size_giubbotto: emp.size_giubbotto || '',
      size_giacca: emp.size_giacca || '',
      size_felpa: emp.size_felpa || '',
      size_tshirt: emp.size_tshirt || '',
      size_camicia: emp.size_camicia || '',
      size_pantalone: emp.size_pantalone || '',
      size_scarpe: emp.size_scarpe || ''
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/employees/${selectedEmployee.id}`, editForm);
      setShowEdit(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = (emp, e) => {
    e.stopPropagation();
    setSelectedEmployee(emp);
    setShowDelete(true);
  };

  const confirmDelete = async (permanent) => {
    try {
      await api.delete(`/employees/${selectedEmployee.id}?permanent=${permanent}`);
      setShowDelete(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const eligibilityBadge = (status) => {
    const map = {
      idoneo: { label: 'Idoneo', cls: 'bg-green-50 text-green-700 border-green-200' },
      idoneo_con_criticita: { label: 'Criticita', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      non_idoneo: { label: 'Non Idoneo', cls: 'bg-red-50 text-red-700 border-red-200' },
    };
    const s = map[status] || map.idoneo;
    return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Personale</h1>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca dipendente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="employee-search"
            />
          </div>
          {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
            <Button onClick={() => setShowCreate(true)} data-testid="add-employee-btn">
              <Plus size={16} className="mr-1" /> Nuovo
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden sm:table-cell">Mansione</TableHead>
                <TableHead className="hidden md:table-cell">Reparto</TableHead>
                <TableHead className="hidden lg:table-cell">Telefono</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Idoneita</TableHead>
                {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && <TableHead className="w-28">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Caricamento...</TableCell></TableRow>
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun dipendente trovato</TableCell></TableRow>
              ) : (
                employees.map((emp) => (
                  <TableRow
                    key={emp.id}
                    className="hover:bg-slate-50 transition-colors"
                    data-testid={`employee-row-${emp.id}`}
                  >
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/employees/${emp.id}`)}>
                      <div>
                        <p className="font-medium text-slate-900">{emp.last_name} {emp.first_name}</p>
                        <p className="text-xs text-slate-500">{emp.fiscal_code}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-600">{emp.role_title || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600">{emp.department || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600">{emp.phone || '-'}</TableCell>
                    <TableCell>
                      {emp.is_active ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><UserCheck size={12} className="mr-1" />Attivo</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200"><UserX size={12} className="mr-1" />Inattivo</Badge>
                      )}
                    </TableCell>
                    <TableCell>{eligibilityBadge(emp.eligibility_status)}</TableCell>
                    {isRole('admin', 'aspp', 'qhse', 'capo_cantiere') && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => navigate(`/employees/${emp.id}`)} title="Dettaglio">
                            <Eye size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => handleEdit(emp, e)} title="Modifica" data-testid={`edit-emp-${emp.id}`}>
                            <Edit size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => handleDelete(emp, e)} className="text-red-600 hover:text-red-700" title="Elimina" data-testid={`delete-emp-${emp.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Dipendente</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Cognome *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Data di Nascita</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="emp-date-of-birth" />
            </div>
            <div className="space-y-1.5">
              <Label>Luogo di Nascita</Label>
              <Input value={form.place_of_birth} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })} data-testid="emp-place-of-birth" placeholder="Es. Roma (RM)" />
            </div>
            <div className="space-y-1.5">
              <Label>Matricola Aziendale</Label>
              <Input value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} data-testid="emp-employee-number" placeholder="Es. MC001" />
            </div>
            <div className="space-y-1.5">
              <Label>Codice Fiscale</Label>
              <Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} data-testid="emp-fiscal-code" />
            </div>
            <div className="space-y-1.5">
              <Label>Mansione</Label>
              <Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} data-testid="emp-role-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Reparto</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="emp-department" />
            </div>
            <div className="space-y-1.5">
              <Label>Livello</Label>
              <Input value={form.contract_level} onChange={(e) => setForm({ ...form, contract_level: e.target.value })} data-testid="emp-level" />
            </div>
            <div className="space-y-1.5">
              <Label>Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Data Assunzione</Label>
              <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-hire-date" />
            </div>
            <div className="col-span-2 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Taglie abbigliamento</div>
            <div className="space-y-1.5">
              <Label>Giubbotto</Label>
              <Input value={form.size_giubbotto} onChange={(e) => setForm({ ...form, size_giubbotto: e.target.value })} data-testid="emp-size-giubbotto" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Giacca</Label>
              <Input value={form.size_giacca} onChange={(e) => setForm({ ...form, size_giacca: e.target.value })} data-testid="emp-size-giacca" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Felpa</Label>
              <Input value={form.size_felpa} onChange={(e) => setForm({ ...form, size_felpa: e.target.value })} data-testid="emp-size-felpa" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>T-shirt</Label>
              <Input value={form.size_tshirt} onChange={(e) => setForm({ ...form, size_tshirt: e.target.value })} data-testid="emp-size-tshirt" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Camicia</Label>
              <Input value={form.size_camicia} onChange={(e) => setForm({ ...form, size_camicia: e.target.value })} data-testid="emp-size-camicia" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Pantalone</Label>
              <Input value={form.size_pantalone} onChange={(e) => setForm({ ...form, size_pantalone: e.target.value })} data-testid="emp-size-pantalone" placeholder="Es. 42" />
            </div>
            <div className="space-y-1.5">
              <Label>Scarpe</Label>
              <Input value={form.size_scarpe} onChange={(e) => setForm({ ...form, size_scarpe: e.target.value })} data-testid="emp-size-scarpe" placeholder="Es. 42" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.first_name || !form.last_name} data-testid="emp-save-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Dipendente</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Nome *</Label>
              <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} data-testid="edit-emp-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Cognome *</Label>
              <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} data-testid="edit-emp-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Data di Nascita</Label>
              <Input type="date" value={editForm.date_of_birth} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Luogo di Nascita</Label>
              <Input value={editForm.place_of_birth} onChange={(e) => setEditForm({ ...editForm, place_of_birth: e.target.value })} placeholder="Es. Roma (RM)" />
            </div>
            <div className="space-y-1.5">
              <Label>Matricola Aziendale</Label>
              <Input value={editForm.employee_number} onChange={(e) => setEditForm({ ...editForm, employee_number: e.target.value })} placeholder="Es. MC001" />
            </div>
            <div className="space-y-1.5">
              <Label>Codice Fiscale</Label>
              <Input value={editForm.fiscal_code} onChange={(e) => setEditForm({ ...editForm, fiscal_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Mansione</Label>
              <Input value={editForm.role_title} onChange={(e) => setEditForm({ ...editForm, role_title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Reparto</Label>
              <Input value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Livello</Label>
              <Input value={editForm.contract_level} onChange={(e) => setEditForm({ ...editForm, contract_level: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Telefono</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Data Assunzione</Label>
              <Input type="date" value={editForm.hire_date} onChange={(e) => setEditForm({ ...editForm, hire_date: e.target.value })} />
            </div>
            <div className="col-span-2 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Taglie abbigliamento</div>
            <div className="space-y-1.5">
              <Label>Giubbotto</Label>
              <Input value={editForm.size_giubbotto} onChange={(e) => setEditForm({ ...editForm, size_giubbotto: e.target.value })} data-testid="edit-emp-size-giubbotto" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Giacca</Label>
              <Input value={editForm.size_giacca} onChange={(e) => setEditForm({ ...editForm, size_giacca: e.target.value })} data-testid="edit-emp-size-giacca" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Felpa</Label>
              <Input value={editForm.size_felpa} onChange={(e) => setEditForm({ ...editForm, size_felpa: e.target.value })} data-testid="edit-emp-size-felpa" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>T-shirt</Label>
              <Input value={editForm.size_tshirt} onChange={(e) => setEditForm({ ...editForm, size_tshirt: e.target.value })} data-testid="edit-emp-size-tshirt" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Camicia</Label>
              <Input value={editForm.size_camicia} onChange={(e) => setEditForm({ ...editForm, size_camicia: e.target.value })} data-testid="edit-emp-size-camicia" placeholder="Es. L" />
            </div>
            <div className="space-y-1.5">
              <Label>Pantalone</Label>
              <Input value={editForm.size_pantalone} onChange={(e) => setEditForm({ ...editForm, size_pantalone: e.target.value })} data-testid="edit-emp-size-pantalone" placeholder="Es. 42" />
            </div>
            <div className="space-y-1.5">
              <Label>Scarpe</Label>
              <Input value={editForm.size_scarpe} onChange={(e) => setEditForm({ ...editForm, size_scarpe: e.target.value })} data-testid="edit-emp-size-scarpe" placeholder="Es. 42" />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-2">
              <input type="checkbox" id="edit-emp-active" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="rounded" />
              <Label htmlFor="edit-emp-active">Dipendente attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.first_name || !editForm.last_name} data-testid="edit-emp-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Employee Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Elimina Dipendente</DialogTitle>
            <DialogDescription>Scegli come procedere con {selectedEmployee?.first_name} {selectedEmployee?.last_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <Button variant="outline" className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => confirmDelete(false)}>
              <UserX size={16} className="mr-2" /> Disattiva (conserva dati)
            </Button>
            <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => confirmDelete(true)}>
              <Trash2 size={16} className="mr-2" /> Elimina definitivamente
            </Button>
            <p className="text-xs text-slate-500 mt-2">L'eliminazione definitiva rimuovera anche documenti, corsi, presenze e ferie associati.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDelete(false)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
